import React, { useRef, useState, useEffect } from 'react';
import './RecentTestData.scss';
import PieChart from '../components/PieChart';
import FullScreenIcon from '../../../features/common/FullScreenIcon/FullScreenIcon';
import DownloadChart from '../../../components/time-series-chart/download/downloadChart';
import { exportChart } from '../../../utilities/ExportChart';
import EmptyState from '../../../components/empty-state/EmptyState';

interface RecentTestsData {
  WellsTestedLast30: number;
  WellsTestedLast60: number;
  WellsTestedOver60: number;
}

interface MainProps {
  recentTestsData: RecentTestsData;
}

export default function RecentTestsData({ recentTestsData }: MainProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const toggleFullscreen = () => {
    if (!containerRef.current) return;

    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch((err) => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
    } else {
      document.exitFullscreen();
    }
  };

  const handleChartDownload = async (option: string) => {
    const element = document.getElementById('recent-tests-content');
    const filename = 'RecentTestsChart';
    if (!element) return;

    // hide only the small action buttons inside the export target to avoid layout reflow
    const fullscreenButton = element.querySelector('.icon-button') as HTMLElement;
    const downloadDropdown = element.querySelector('.download-chart-dropdown') as HTMLElement;

    if (fullscreenButton) fullscreenButton.style.display = 'none';
    if (downloadDropdown) downloadDropdown.style.display = 'none';

    try {
      await exportChart(element, getExportType(option), filename, undefined, undefined, 'Recent tests');
    } finally {
      if (fullscreenButton) fullscreenButton.style.display = '';
      if (downloadDropdown) downloadDropdown.style.display = '';
    }
  };

  const getExportType = (option: string): 'JPG' | 'PDF' => (option === 'Download as PDF' ? 'PDF' : 'JPG');

  // Transform the prop data into chart format
  const chartData = [
    { category: 'Last 30 days', value: recentTestsData.WellsTestedLast30, color: '#003747' },
    { category: '30-60 days', value: recentTestsData.WellsTestedLast60, color: '#005F7C' },
    { category: '> 60 days', value: recentTestsData.WellsTestedOver60, color: '#00789E' },
  ];

  // Check if data is empty or all values are zero
  const hasData = chartData && chartData.length > 0 && chartData.some((item) => item.value > 0);
  return (
    <div ref={containerRef} className={`recent-tests-widget ${isFullscreen ? 'fullscreen' : ''}`}>
      <div className='widget-content'>
        <header className='widget-header'>
          <div className='title-section'>
            <h1 className='section-title'>Recent tests</h1>
          </div>
          <div className='header-actions'>
            <button className='icon-button' onClick={toggleFullscreen} analytics-id='recent-tests-fullscreen-btn'>
              <FullScreenIcon />
            </button>
            <button className='icon-button' analytics-id='recent-tests-download-btn'>
              <DownloadChart
                onClick={(option) => handleChartDownload(option)}
                canExportJpg={true}
                canExportPdf={true}
              />
            </button>
          </div>
        </header>
        <div className='divider' />
        <div id='recent-tests-content'>
          <section className='chart-section'>
            {hasData ? (
              <>
                <div className='chart-title'>Well test frequency</div>
                <div className='chart-container'>
                  <div className='chart-wrapper'>
                    <PieChart data={chartData} showHeader={false} />
                  </div>
                  <div className='legend-wrapper' style={isFullscreen ? { marginLeft: '40%' } : {}}>
                    {chartData.map((item, index) => (
                      <div key={index} className='legend-item'>
                        <div className='legend-dot' style={{ backgroundColor: item.color }} />
                        <span className='legend-text'>{item.category}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <EmptyState message='No recent test data available' />
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
